import { computed, reactive } from 'vue';
let i18nOverrides = {};
let i18nHasLoaded = false;
let i18n = reactive({});

// @TODO include date-fn locales and split to separate files
function loadI18n() {
  return import(/* webpackChunkName: "locales" */'@/i18n/index.js')
    .then((module) => {
      Object.assign(i18n, module.default);
      i18nHasLoaded = true;
      if (Object.keys(i18nOverrides).length > 0) {
        applyi18nOverrides(i18nOverrides);
        i18nOverrides = {};
      }
    })
    .catch(() => {
      console.error('Failed to load i18n');
    });
}
loadI18n();

const usedPhrases = new Set();

function t (phrase, disableLanguageFlow) {
  usedPhrases.add(phrase);

  if (state.phraseFinderMode) {
    return phrase;
  }
  const value = state.i18n[phrase];
  if (! value) {
    return null;
  }
  if (state.lang in value) {
    return value[state.lang];
  }
  if (disableLanguageFlow) {
    return null;
  }
  return value.en || null;
}

t.prefix = function (prefix, disableLanguageFlow) {
  return phrase => t(prefix + '.' + phrase, disableLanguageFlow);
}

function applyi18nOverrides (overrides) {
  if (! i18nHasLoaded) {
    Object.assign(i18nOverrides, overrides);
    return;
  }
  for (const key in overrides) {
    const split = key.split('.');

    const lang = split.shift();
    const phrase = split.join('.');

    if (!state.i18n[phrase]) {
      state.i18n[phrase] = {};
    }

    state.i18n[phrase][lang] = overrides[key];
  }
}

import {
  de as date_de,
  sv as date_sv,
  enUS as date_en,
  da as date_da,
  fi as date_fi,
  nb as date_nb,
  pl as date_pl,
  nl as date_nl,
  fr as date_fr,
} from 'date-fns/locale';

const locales = {
  en_US: {
    lang: 'en',
    dateLocale: date_en,
  },
  sv_SE: {
    lang: 'sv',
    dateLocale: date_sv,
  },
  da_DK: {
    lang: 'da',
    dateLocale: date_da,
  },
  no_NO: {
    lang: 'no',
    dateLocale: date_nb,
  },
  nb_NO: {
    lang: 'no',
    altLangugaes: ['nb'],
    dateLocale: date_nb,
  },
  fi_FI: {
    lang: 'fi',
    dateLocale: date_fi,
  },
  nl_NL: {
    lang: 'nl',
    dateLocale: date_nl,
  },
  pl_PL: {
    lang: 'pl',
    dateLocale: date_pl,
  },
  de_DE: {
    lang: 'de',
    dateLocale: date_de,
  },
  de_AT: {
    lang: 'de',
    dateLocale: date_de,
  },
  fr_FR: {
    lang: 'fr',
    dateLocale: date_fr,
  },
};


function setLocale (locale) {
  locale = locale.replace('-', '_');
  if (locale in locales) {
    state.locale = locales[locale];
    state.lang = state.locale.lang;
  } else {
    console.error('Locale "' + locale + '" not supported');
  }
}

function setLocaleViaLang(lang) {
  // Iterate over the locales object
  for (const locale in locales) {
    if (locales[locale].lang === lang) {
      // If a match is found, set the locale and return
      setLocale(locale);
      return;
    }

    if (locales[locale].altLangugaes && locales[locale].altLangugaes.includes(lang)) {
      setLocale(locale);
      return;
    }
  }
  // If no match is found, log an error
  console.error('Language "' + lang + '" not supported');
}

function repairCustomerToken (value) {
  if (! value) {
    return null;
  } else if (value === 'null') {
    console.error('Customer token was set to string value "null", use actual null value instead');
    return null;
  } else if (typeof value === 'string') {
    return value.replace(/\s/g, '+');
  }
  throw new Error('Invalid customer token format');
}

const gmf_config = window.gmf_config; // Stores initial state

const state = reactive({
  setLocale,
  setLocaleViaLang,
  phraseFinderMode: false,

  merchantId: ~~gmf_config.merchantId,
  customerToken: repairCustomerToken(gmf_config.customerToken),
  apiBase: gmf_config.apiBase,
  cdnBase: gmf_config.cdnBase,

  locale: locales.sv_SE,
  lang: 'sv', // @TODO read from locale instead

  i18n,
  t,

  applyi18nOverrides,

  merchantConfig: window.gmf_config.merchantConfig,
  tokenBalances: null,

  avatarTimestamps: {},
});

// Debug stuff for finding unused phrases
if (process.env.NODE_ENV === 'development') {
  state.exportUsedPhrases = function () {
    const arr = Array.from(usedPhrases);
    // sort
    arr.sort();
    console.log(arr.join('\n'));
    console.log(arr.length + ' used phrases');
  },
  state.listUnusedPhrases = function () {
    const allPhrases = Object.keys(state.i18n);
    const unusedPhrases = allPhrases.filter(phrase => !usedPhrases.has(phrase));
    // sort
    unusedPhrases.sort();
    console.log(unusedPhrases.join('\n'));
    console.log(unusedPhrases.length + ' unused phrases');
  },
  state.importUsedPhrases = function (phrases) {
    phrases.split(/\s+/g).forEach(phrase => usedPhrases.add(phrase));
    console.log('Total ' + usedPhrases.size + ' used phrases');
  },
  state.clearUsedPhrases = function () {
    usedPhrases.clear();
    localStorage.removeItem('gmf_used_phrases');
  },
  state.saveUsedPhrases = function () {
    state.loadUsedPhrases();
    localStorage.setItem('gmf_used_phrases', Array.from(usedPhrases).join('\n'));
    console.log('Saved ' + usedPhrases.size + ' used phrases');
  },
  state.loadUsedPhrases = function () {
    const phrases = localStorage.getItem('gmf_used_phrases');
    if (phrases) {
      phrases.split(/\s+/g).forEach(phrase => usedPhrases.add(phrase));
      console.log('Loaded ' + usedPhrases.size + ' used phrases');
    } else {
      console.log('No used phrases to load');
    }
  }
}



state.commentSearchEndpoint = computed(() => {
  return '/v3/comments/search';
});

if (gmf_config && gmf_config.locale) {
  setLocale(gmf_config.locale);
}

if (gmf_config && gmf_config.lang) {
  setLocaleViaLang(gmf_config.lang);
}

state.isLoggedIn = function () {
  return state.customerToken && state.customerToken.length > 10;
};

window._gmf_debug = {
  state: state,
};

export default state;
